// frontend/src/components/LandingPage/LandingNavbar.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth'; // Adjusted path
import { motion } from 'framer-motion'; // Assuming motion might be used, or can be removed if not
import logo from '../../assets/logo.svg';

// Helper for smooth scroll - ensure this is available or passed as prop if needed
// For now, keeping it simple and assuming direct navigation or top scroll for auth pages
const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    } else {
        // On auth pages, these elements might not exist, so direct navigation is primary.
        // console.warn(`Smooth scroll target '${elementId}' not found.`);
    }
};

const LandingNavbar = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate(); // useNavigate can be used for direct navigation

    // Navigation links for the landing page (can be adjusted if different for auth pages)
    const navLinks = [
        { href: "/#features", label: "How It Works", id: "features" },
        { href: "/#cta", label: "Get Started", id: "cta" }
    ];

    const handleLinkClick = (e, href, targetId) => {
        e.preventDefault();
        // If it's a hash link for the current page (e.g. landing page sections)
        if (href.startsWith('/#') && window.location.pathname === '/') {
            smoothScrollTo(targetId);
        } else if (href.startsWith('/#')) {
            // If on an auth page, navigate to landing page then scroll
            navigate('/');
            setTimeout(() => smoothScrollTo(targetId), 0); // Allow time for navigation
        }
        else {
            navigate(href); // For direct links like /login, /register
        }
        setMobileMenuOpen(false);
    };
    
    // Effect to close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [navigate]); // Listening to navigate object which changes on navigation

    return (
        <nav className="bg-white py-4 fixed w-full z-20 shadow-sm border-b border-gray-200">
            <div className="container mx-auto px-6 flex justify-between items-center">
                {/* Desktop: Logo Left */}
                <div className="flex items-center">
                    <Link
                        to="/"
                        onClick={(e) => {
                            if (window.location.pathname === '/') {
                                e.preventDefault();
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else {
                                navigate('/'); // Navigate to home if not already there
                            }
                        }}
                        className="flex items-center space-x-2 hover:opacity-90 transition-opacity mr-6"
                        >
                            <img src={logo} alt="Agent Digest" className="h-8 md:h-10 w-auto" />
                            <span className="text-xl md:text-2xl font-bold text-blue-600">Agent Digest</span>
                    </Link>
                </div>

                {/* Desktop: Center Nav Links */}
                <div className="hidden md:flex flex-grow justify-center items-center space-x-6">
                    {navLinks.map(link => (
                        <a
                            key={link.label}
                            href={link.href}
                            onClick={(e) => handleLinkClick(e, link.href, link.id)}
                            className="text-gray-600 hover:text-blue-600 transition duration-150 cursor-pointer"
                        >
                            {link.label}
                        </a>
                    ))}
                </div>

                {/* Desktop: Right Auth Buttons */}
                <div className="hidden md:flex items-center space-x-4">
                    {isAuthenticated ? (
                        <Link
                            to="/article-feed"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-lg transition duration-150 ease-in-out shadow-md hover:shadow-lg text-sm"
                        >
                            Go to Article Feed
                        </Link>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                className="text-gray-600 hover:text-blue-600 transition duration-150 text-sm font-medium"
                            >
                                Log In
                            </Link>
                            <Link
                                to="/register"
                                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-lg transition duration-150 ease-in-out shadow-md hover:shadow-lg text-sm"
                            >
                                Get Started
                            </Link>
                        </>
                    )}
                </div>

                {/* Mobile: Logo Left, Burger Right */}
                <div className="md:hidden flex justify-between items-center w-full">
                    <Link
                        to="/"
                        onClick={(e) => {
                            if (window.location.pathname === '/') {
                                e.preventDefault();
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            } else {
                                navigate('/');
                            }
                            setMobileMenuOpen(false);
                        }}
                        className="flex items-center space-x-2 hover:opacity-90 transition-opacity"
                    >
                        <img src={logo} alt="Agent Digest" className="h-8 w-auto" />
                        <span className="text-xl font-bold text-blue-600">Agent Digest</span>
                    </Link>
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="text-gray-600 hover:text-blue-600 focus:outline-none p-2"
                        aria-label="Toggle mobile menu"
                        aria-expanded={mobileMenuOpen}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}></path></svg>
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="md:hidden bg-white shadow-lg py-2 absolute w-full border-t border-gray-200">
                    {navLinks.map(link => (
                         <a
                            key={link.label}
                            href={link.href}
                            onClick={(e) => handleLinkClick(e, link.href, link.id)}
                            className="block px-6 py-3 text-gray-600 hover:bg-blue-50 hover:text-blue-600 cursor-pointer"
                        >
                            {link.label}
                        </a>
                    ))}
                    <hr className="my-1 border-gray-100"/>
                    {isAuthenticated ? (
                        <Link
                            to="/article-feed"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 text-center font-semibold rounded-lg mx-4 my-2"
                        >
                            Go to Article Feed
                        </Link>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                onClick={() => setMobileMenuOpen(false)}
                                className="block px-6 py-3 text-gray-600 hover:bg-blue-50 hover:text-blue-600"
                            >
                                Log In
                            </Link>
                            <Link
                                to="/register"
                                onClick={() => setMobileMenuOpen(false)}
                                className="block px-6 py-3 text-white bg-blue-600 hover:bg-blue-700 text-center font-semibold rounded-lg mx-4 my-2"
                            >
                                Get Started
                            </Link>
                        </>
                    )}
                </div>
            )}
        </nav>
    );
};

export default LandingNavbar;
